import React from 'react';
import { Form, Field } from 'react-final-form';
import {
  FormGroup,
  InputGroup,
  Button,
  ButtonToolbar,
  ControlLabel,
  FormControl,
  HelpBlock,
} from 'react-bootstrap';
import Select from 'react-select';

import { composeValidators, required, validPercentage, positiveNumber } from '../../../forms';
import PercentInput from '../../../forms/PercentInput';

const Error = ({ name }) => (
  <Field
    name={name}
    subscription={{ touched: true, error: true }}
    render={({ meta: { touched, error } }) =>
      touched && error ? <span className="alert-danger">{error}</span> : null
    }
  />
);

export const renderTextField = ({ input, label, type, meta: { touched, error, warning } }) => (
  <InputGroup>
    {label && <ControlLabel>{label}</ControlLabel>}
    <FormControl {...input} type={type} />
  </InputGroup>
);

const RenderSelectInput = ({
  input,
  options,
  name,
  id,
  label,
  help,
  meta: { active, touched, error, warning },
}) => (
  <FormGroup>
    <ControlLabel>{label}</ControlLabel>
    <Select
      id={id}
      name={name}
      options={options}
      value={input.value}
      onChange={value => {
        input.onChange(value.value);
      }}
      onBlur={value => input.onBlur(value.value)}
      joinValues
    />
    {help && <HelpBlock>{help}</HelpBlock>}
    {touched &&
      ((error && <p className="alert alert-danger">{error}</p>) ||
        (warning && <p className="alert alert-warning">{warning}</p>))}
  </FormGroup>
);
//GBP, EURO, AUD, JPY, CNY
const currencyOptions = [
  {
    label: '🇦🇺 AUD - Australian Dollar',
    value: 'AUD',
  },
  {
    label: '🇨🇦 CAD - Canadian Dollar',
    value: 'CAD',
  },
  {
    label: '🇨🇳 CNY - Chinese Yuan Renminbi',
    value: 'CNY',
  },
  {
    label: '🇪🇺 EUR - Euro',
    value: 'EUR',
  },
  {
    label: '🇬🇧 GBP - British Pound',
    value: 'GBP',
  },
  {
    label: '🇯🇵 JPY - Japanese Yen',
    value: 'JPY',
  },
  {
    label: '🇲🇽 MXN - Mexican Peso',
    value: 'MXN',
  },
  {
    label: '🇺🇸 USD - US Dollar',
    value: 'USD',
  },
];

const hourOptions = [
  {
    label: '10:00 am',
    value: '10:00:00',
  },
  {
    label: '10:30 am',
    value: '10:30:00',
  },
  {
    label: '11:00 am',
    value: '11:00:00',
  },
  {
    label: '11:30 am',
    value: '11:30:00',
  },
  {
    label: '12:00 pm',
    value: '12:00:00',
  },
  {
    label: '12:30 pm',
    value: '12:30:00',
  },
  {
    label: '1:00 pm',
    value: '13:00:00',
  },
  {
    label: '2:00 pm',
    value: '14:00:00',
  },
  {
    label: '3:00 pm',
    value: '15:00:00',
  },
  {
    label: '3:30 pm',
    value: '15:30:00',
  },
  {
    label: '4:00 pm',
    value: '16:00:00',
  },
  {
    label: '4:30 pm',
    value: '16:30:00',
  },
  {
    label: '5:00 pm',
    value: '17:00:00',
  },
];

const starOptions = [
  {
    label: '⭐',
    value: '1',
  },
  {
    label: '⭐⭐',
    value: '2',
  },
  {
    label: '⭐⭐⭐',
    value: '3',
  },
  {
    label: '⭐⭐⭐⭐',
    value: '4',
  },
  {
    label: '⭐⭐⭐⭐⭐',
    value: '5',
  },
];

const PropertyDetailsForm = ({ onSubmit, initialValues, options, handleClose }) => (
  <Form
    onSubmit={onSubmit}
    initialValues={initialValues}
    validate={values => {
      const errors = {};
      if (!values.name) {
        errors.FORM_ERROR = 'Hotel name must not be blank.';
      }
      return errors;
    }}
    render={({ handleSubmit, reset, submitting, pristine, errors }) => (
      <form onSubmit={handleSubmit}>
        <div className="row">
          <div className="col-md-12 hotel-details-description">
            <h4 className="hotel-highlight">Details</h4>
          </div>
          <div className="col-md-4">
            <FormGroup>
              <Field
                name="stars"
                component={RenderSelectInput}
                label="AAA Rating"
                options={starOptions}
              />
              <Field
                name="check_in"
                component={RenderSelectInput}
                options={hourOptions}
                label="Check-in Time"
              />
              <Field
                name="check_out"
                component={RenderSelectInput}
                options={hourOptions}
                label="Check-out Time"
              />
              <Field name="room_count" component={renderTextField} label="Number of Rooms" />
              <Field name="floors" component={renderTextField} label="Number of Floors" />
            </FormGroup>
          </div>
          <div className="col-md-4">
            <FormGroup>
              <Field
                name="currency"
                component={RenderSelectInput}
                label="Currency"
                options={currencyOptions}
              />
              <Field
                name="tax_rate"
                component={PercentInput}
                placeholder="12.0%"
                label="Tax Rate"
                validate={composeValidators(required, validPercentage)}
              />
              <Error name="tax_rate" />
              <Field name="resort_fee" component={renderTextField} label="Resort Fee" />
            </FormGroup>
          </div>
        </div>
        <div className="row">
          <div className="col-md-12">
            <FormGroup>
              <h4 className="hotel-highlight">Bus Parking</h4>
              <Field
                name="bus_capacity"
                component={renderTextField}
                placeholder="0"
                label="Number of Buses"
              />
              <Field
                name="bus_parking"
                component={renderTextField}
                placeholder="Please enter notes about the bus parking. Cost per bus. If offsite, how far way."
                label="Parking Notes"
              />
            </FormGroup>
          </div>
        </div>
        <div className="row">
          <div className="col-md-12">
            {errors.FORM_ERROR && <p className="alert alert-danger">{errors.FORM_ERROR}</p>}
            <FormGroup>
              <ButtonToolbar>
                <Button disabled={pristine || submitting} bsStyle="success" type="submit">
                  Save
                </Button>
                <Button disabled={submitting} bsStyle="success" onClick={handleClose}>
                  Cancel
                </Button>
              </ButtonToolbar>
            </FormGroup>
          </div>
        </div>
      </form>
    )}
  />
);

export default PropertyDetailsForm;



// WEBPACK FOOTER //
// src/routes/hotels/components/PropertyDetailsForm.js